import React, { useState, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import { format, differenceInCalendarDays, addDays, startOfDay, isSameDay, isBefore } from 'date-fns';
import { supabase } from '../../lib/supabase';
import 'react-day-picker/style.css';

export default function BookingCalendar({ equipmentData, onSelect, onBack, onChangeDelivery, editCartHref }) {
     const [range, setRange] = useState({ from: null, to: null });
     const [unavailableDates, setUnavailableDates] = useState([]);
     const [loading, setLoading] = useState(false);

     const dailyRateDollars = equipmentData
          ? equipmentData.dailyRateCents / 100
          : 650;

     const deliveryAvailable = (equipmentData?.items || []).some((i) => i.delivery_available);
     const deliveryFeeBaseCents = (equipmentData?.items || [])
          .filter((i) => i.delivery_available)
          .reduce((sum, i) => sum + (i.delivery_fee || 0) * (i.selectedQty || 1), 0);
     const currentDeliveryMethod = equipmentData?.delivery?.method || 'pickup';

     useEffect(() => {
          fetchUnavailableDates();
          // eslint-disable-next-line react-hooks/exhaustive-deps
     }, []);

     // Parse a 'YYYY-MM-DD' string as a local-time Date (not UTC).
     // new Date('2026-04-30') resolves to UTC midnight, which in UTC-7 lands on April 29 evening.
     function parseLocalDate(yyyymmdd) {
          const [y, m, d] = yyyymmdd.split('-').map(Number);
          return new Date(y, m - 1, d);
     }

     async function fetchUnavailableDates() {
          setLoading(true);
          try {
               const { data: bookings } = await supabase
                    .from('bookings')
                    .select('start_date, end_date')
                    .in('status', ['confirmed', 'pending']);

               const { data: blocked } = await supabase
                    .from('blocked_dates')
                    .select('date');

               const dates = [];
               if (bookings) {
                    bookings.forEach((booking) => {
                         let current = parseLocalDate(booking.start_date);
                         const end = parseLocalDate(booking.end_date);
                         while (current <= end) {
                              dates.push(current);
                              current = addDays(current, 1);
                         }
                    });
               }
               if (blocked) {
                    blocked.forEach((b) => dates.push(parseLocalDate(b.date)));
               }

               setUnavailableDates(dates);
          } catch (error) {
               console.error('Error fetching availability:', error);
          }
          setLoading(false);
     }

     function handleSelect(selectedRange) {
          setRange(selectedRange || { from: null, to: null });
     }

     function handleConfirm() {
          if (range.from && range.to) {
               const days = differenceInCalendarDays(range.to, range.from) + 1;
               onSelect({
                    startDate: range.from,
                    endDate: range.to,
                    days,
               });
          }
     }

     const today = startOfDay(new Date());
     const days = range.from && range.to
          ? differenceInCalendarDays(range.to, range.from) + 1
          : 0;

     return (
          <div className='w-full max-w-3xl mx-auto'>
               <h3 className='text-dark uppercase font-bold text-[32px] laptop:text-[40px] leading-tight mb-2'>
                    Select Your Rental Dates
               </h3>
               <p className='text-darker/70 mb-8'>
                    Choose a start and end date for your rental.
                    {equipmentData && (
                         <span className='text-yellow font-semibold ml-1'>
                              (${dailyRateDollars.toLocaleString()}/day)
                         </span>
                    )}
               </p>

               <div className='bg-white rounded-2xl p-6 border border-gray-200 shadow-sm'>
                    {loading && (
                         <div className='text-darker/40 text-sm text-center mb-2'>
                              Checking availability...
                         </div>
                    )}
                    <DayPicker
                         mode='range'
                         selected={range}
                         onSelect={handleSelect}
                         disabled={(date) => {
                              const earliest = addDays(today, 1); // bookings must start tomorrow or later
                              if (isBefore(date, earliest)) return true;
                              return unavailableDates.some((d) => isSameDay(d, date));
                         }}
                         numberOfMonths={2}
                         modifiersClassNames={{
                              today: 'rdp-today-override',
                         }}
                         classNames={{
                              months: 'flex flex-col tablet:flex-row gap-8',
                              root: 'text-darker',
                              day: 'text-darker hover:bg-yellow/20 rounded-lg p-2',
                              selected: 'bg-yellow text-dark font-bold',
                              range_start: 'bg-yellow text-dark rounded-l-lg',
                              range_end: 'bg-yellow text-dark rounded-r-lg',
                              range_middle: 'bg-yellow/30 text-darker',
                              disabled: 'text-darker/30 line-through cursor-not-allowed',
                              caption: 'text-dark font-semibold text-lg',
                              nav_button: 'text-yellow hover:text-dark',
                              head_cell: 'text-darker/40 font-normal',
                         }}
                    />

                    {range.from && (
                         <div className='mt-4 pt-4 border-t border-gray-200'>
                              <div className='text-darker'>
                                   <span className='text-yellow font-semibold'>
                                        {format(range.from, 'MMM d, yyyy')}
                                   </span>
                                   {range.to && (
                                        <>
                                             {' → '}
                                             <span className='text-yellow font-semibold'>
                                                  {format(range.to, 'MMM d, yyyy')}
                                             </span>
                                             <span className='text-darker/60 ml-2'>
                                                  ({days} {days === 1 ? 'day' : 'days'} · ${(days * dailyRateDollars).toLocaleString()})
                                             </span>
                                        </>
                                   )}
                              </div>
                         </div>
                    )}
               </div>

               {/* Delivery toggle */}
               {deliveryAvailable && onChangeDelivery && (
                    <div className='mt-6 bg-white rounded-2xl p-5 border border-gray-200'>
                         <p className='text-darker font-semibold mb-3'>
                              How would you like to receive your equipment?
                         </p>
                         <div className='grid grid-cols-2 gap-3'>
                              <button
                                   type='button'
                                   onClick={() => onChangeDelivery('pickup')}
                                   className={`py-3 px-4 rounded-xl text-sm font-semibold border-2 duration-300 text-left ${
                                        currentDeliveryMethod === 'pickup'
                                             ? 'border-yellow bg-yellow/10 text-darker'
                                             : 'border-gray-200 text-darker/60 hover:border-darker/30'
                                   }`}
                              >
                                   <span className='block text-base'>Self Pickup</span>
                                   <span className='text-darker/50 text-xs font-normal'>
                                        Free · East Mesa location
                                   </span>
                              </button>
                              <button
                                   type='button'
                                   onClick={() => onChangeDelivery('delivery')}
                                   className={`py-3 px-4 rounded-xl text-sm font-semibold border-2 duration-300 text-left ${
                                        currentDeliveryMethod === 'delivery'
                                             ? 'border-yellow bg-yellow/10 text-darker'
                                             : 'border-gray-200 text-darker/60 hover:border-darker/30'
                                   }`}
                              >
                                   <span className='block text-base'>
                                        Delivery — +${(deliveryFeeBaseCents / 100).toLocaleString()}
                                   </span>
                                   <span className='text-darker/50 text-xs font-normal'>
                                        To your job site
                                   </span>
                              </button>
                         </div>
                    </div>
               )}

               <div className='mt-8 flex gap-4 justify-between'>
                    {onBack ? (
                         <button
                              onClick={onBack}
                              className='py-3 px-6 rounded-xl border-2 border-gray-200 text-darker/60 font-semibold hover:border-darker/30 hover:text-darker duration-300'
                         >
                              ← Back
                         </button>
                    ) : (
                         <a
                              href={editCartHref || '/pricing'}
                              className='py-3 px-6 rounded-xl border-2 border-gray-200 text-darker/60 font-semibold hover:border-darker/30 hover:text-darker duration-300'
                         >
                              ← Edit Cart
                         </a>
                    )}
                    {range.to && (
                         <PrimaryPillButton onClick={handleConfirm}>
                              Continue
                         </PrimaryPillButton>
                    )}
               </div>
          </div>
     );
}

function PrimaryPillButton({ onClick, type = 'button', children, disabled }) {
     return (
          <button
               type={type}
               onClick={onClick}
               disabled={disabled}
               className='group relative font-semibold text-base uppercase tracking-wider bg-yellow text-dark pl-10 pr-6 py-4 rounded-xl hover:bg-yellow/90 duration-300 flex items-center disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap'
          >
               <span className='text-dark pr-2 absolute left-4 duration-300 group-hover:tracking-[0.15em]'>
                    {'//'}
               </span>
               {children}
          </button>
     );
}
